/**
 * Deep-link action contract.
 *
 * A single enumeration of "things you can ask the mobile app to do
 * from a link". Shared between the builder (this app) and the
 * parser (citizen-mobile's `core/lib/deep-links.ts`).
 *
 * If you add a new action here, you MUST also:
 *   1. Add a corresponding router push in the mobile app's
 *      `use<Hero>DeepLinks` hook (or `core/lib/deep-links.ts`).
 *   2. Add a handler to the `/l/*` web landing page so users without
 *      the app installed still get a reasonable web fallback.
 *
 * Shape of the URL:
 *   https://citizen.tonnie.ai/l/<action>?<params>
 *
 * Where `<action>` is a slash-separated path ("care/checkpoint",
 * "qultureup/mission", …). Slashes are preserved so Universal Links
 * can pattern-match on prefix; flat strings would work too but
 * hierarchical reads better in logs and email previews.
 */

// ─── Canonical action keys ─────────────────────────────────────────

export const DeepLinkActions = {
  /** Open the TelePortal tab. Optional `taskId` for direct task. */
  TELEPORTAL: 'teleportal',
  /** Open a specific TelePortal task by id. Requires `entityId`. */
  TELEPORTAL_TASK: 'teleportal/task',
  /** Open a TelePortal document. Requires `entityId`. */
  TELEPORTAL_DOCUMENT: 'teleportal/document',

  /** Open the QultureUp tab. */
  QULTUREUP: 'qultureup',
  /** Open a mission. Requires `entityId`. */
  QULTUREUP_MISSION: 'qultureup/mission',
  /** Open a world. Requires `entityId`. */
  QULTUREUP_WORLD: 'qultureup/world',

  /** Open the Care tab. */
  CARE: 'care',
  /** Open a specific Care checkpoint. Requires `entityId`. */
  CARE_CHECKPOINT: 'care/checkpoint',
  /** Start a Care quick check-in. */
  CARE_QUICK_CHECKIN: 'care/quick-checkin',
  /** Open the Care assessment intake. */
  CARE_ASSESSMENT: 'care/assessment',
  /** Open the Care objectives self-evaluation (BL-125). */
  CARE_OBJECTIVES: 'care/objectives',

  /** Open the Passport tab. */
  PASSPORT: 'passport',
  /** Open a specific passport stamp. Requires `entityId`. */
  PASSPORT_STAMP: 'passport/stamp',

  /** Open the CheckUp hero. */
  CHECKUP: 'checkup',

  /** Open InOut — its tab while it runs, its start otherwise. */
  INOUT: 'inout',
  /**
   * A calculation a portal's calculator handed to Tonnie, kept in the app
   * (D1-B): the phone that cannot use Citizen Web carries it there. Requires
   * `token` (the one-time hand-off token, 72 h).
   */
  INOUT_HANDOFF: 'inout/handoff',
} as const;

export type DeepLinkAction = (typeof DeepLinkActions)[keyof typeof DeepLinkActions];

/** Every known action string, for exhaustive switches + the landing page. */
export const ALL_DEEP_LINK_ACTIONS: DeepLinkAction[] = Object.values(DeepLinkActions);

// ─── Param shape ───────────────────────────────────────────────────

/**
 * Common params every deep link may carry. Purely conventional —
 * the builder + parser don't enforce which actions take which
 * fields; the mobile hooks pull what they need.
 */
export interface DeepLinkParams {
  /** Optional id of the specific entity the link points at. */
  entityId?: string;
  /** Internal user id — included so the mobile side can re-auth
   *  or verify it's the intended recipient. */
  userId?: string;
  /** Active journey instance id (lets mobile skip a tenant lookup). */
  journeyInstanceId?: string;
  /** ISO country codes for the passport flow. */
  originCountry?: string;
  destinationCountry?: string;
  /** Scenario (e.g. `"work"`, `"study"`) for the passport flow. */
  scenario?: string;
  /** When the link was minted — lets mobile reject stale QR codes
   *  (configured TTL is 24 h by default). */
  timestamp?: string;
  /** InOut's one-time hand-off token (`inout/handoff`). */
  token?: string;
  /** Caller-supplied extra context; arbitrary key/value. */
  [extra: string]: string | undefined;
}
